import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faUser } from '@fortawesome/free-solid-svg-icons';

import PageHeader from '../../components/common/PageHeader';
import LoginService from '../../services/LoginService';
import ProfileService from '../../services/ProfileService';
import PasswordInputField from '../../components/common/PasswordInputField';
import ImageUploaderWithCrop from '../../components/utils/ImageUploaderWithCrop';
import { convertToImageUrl } from '../../utils/imageUrlHelper';
import { useNotification } from '../../contexts/NotificationContext';

const Profile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success: notifySuccess, error: notifyError } = useNotification();

  const [activeTab, setActiveTab] = useState('info');

  const [profileForm, setProfileForm] = useState({
    fullName: '',
    email: '',
    documentNumber: '',
    phone: '',
  });
  const [profileInitialData, setProfileInitialData] = useState({
    fullName: '',
    email: '',
    documentNumber: '',
    phone: '',
  });
  const [profileSuccessMessage, setProfileSuccessMessage] = useState('');
  const [profileErrorMessage, setProfileErrorMessage] = useState('');
  const [profileImage, setProfileImage] = useState('');
  const [profileInitialImage, setProfileInitialImage] = useState('');
  const [isEditingProfileImage, setIsEditingProfileImage] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ password: '', confirmPassword: '' });
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({});
  const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
  const [passwordSuccessMessage, setPasswordSuccessMessage] = useState('');
  const [isPasswordSubmitting, setIsPasswordSubmitting] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await ProfileService.getCurrentProfile();
        const data = {
          fullName: profile?.fullName || '',
          email: profile?.email || '',
          documentNumber: profile?.documentNumber || '',
          phone: profile?.phone || '',
        };
        setProfileForm(data);
        setProfileInitialData(data);
        const image = convertToImageUrl(profile?.profileImage || '', '');
        setProfileImage(image);
        setProfileInitialImage(image);
        setIsEditingProfileImage(false);
      } catch (error) {
        setProfileErrorMessage(error?.message || t('common.error'));
      }
    };
    loadProfile();
  }, [t]);

  useEffect(() => {
    if (profileErrorMessage) {
      notifyError(profileErrorMessage, { autoClose: 3000, placement: 'centered' });
      setProfileErrorMessage('');
    }
  }, [profileErrorMessage, notifyError]);

  useEffect(() => {
    if (profileSuccessMessage) {
      notifySuccess(profileSuccessMessage, { autoClose: 3000, placement: 'centered' });
      setProfileSuccessMessage('');
    }
  }, [profileSuccessMessage, notifySuccess]);

  useEffect(() => {
    if (passwordErrorMessage) {
      notifyError(passwordErrorMessage, { autoClose: 3000, placement: 'centered' });
      setPasswordErrorMessage('');
    }
  }, [passwordErrorMessage, notifyError]);

  useEffect(() => {
    if (passwordSuccessMessage) {
      notifySuccess(passwordSuccessMessage, { autoClose: 3000, placement: 'centered' });
      setPasswordSuccessMessage('');
    }
  }, [passwordSuccessMessage, notifySuccess]);

  const handleProfileChange = (event) => {
    const { name, value } = event.target;
    setProfileForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveProfile = async () => {
    setProfileSuccessMessage('');
    setProfileErrorMessage('');
    try {
      const payload = new FormData();
      payload.append('fullName', profileForm.fullName || '');
      payload.append('email', profileForm.email || '');
      payload.append('documentNumber', profileForm.documentNumber || '');
      payload.append('phone', profileForm.phone || '');

      if (isEditingProfileImage && typeof profileImage === 'string' && profileImage.startsWith('data:image/')) {
        const blob = await fetch(profileImage).then((r) => r.blob());
        payload.append('profileImage', blob, 'avatar.png');
      }

      const updated = await ProfileService.updateCurrentProfile(payload);
      const normalized = {
        fullName: updated?.fullName || '',
        email: updated?.email || '',
        documentNumber: updated?.documentNumber || '',
        phone: updated?.phone || '',
      };
      const normalizedImage = convertToImageUrl(updated?.profileImage || profileImage || '', '');
      setProfileForm(normalized);
      setProfileInitialData(normalized);
      setProfileImage(normalizedImage);
      setProfileInitialImage(normalizedImage);
      setIsEditingProfileImage(false);

      const storedUserDataStr = localStorage.getItem('userData');
      const storedUserData = storedUserDataStr ? JSON.parse(storedUserDataStr) : {};
      const updatedUserData = {
        ...storedUserData,
        name: normalized.fullName,
        username: normalized.email,
        email: normalized.email,
        personFullName: normalized.fullName,
        personDocument: normalized.documentNumber,
        personPhone: normalized.phone,
      };
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
      localStorage.setItem('displayName', normalized.fullName);
      localStorage.setItem('username', normalized.email);
      window.dispatchEvent(new CustomEvent('profileUpdated', {
        detail: { displayName: normalized.fullName, profileImage: normalizedImage }
      }));
      setProfileSuccessMessage(t('profile.success.saved'));
    } catch (error) {
      setProfileErrorMessage(error?.message || t('common.error'));
    }
  };

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    await saveProfile();
  };

  const handleProfileReset = () => {
    setProfileForm(profileInitialData);
    setProfileImage(profileInitialImage);
    setIsEditingProfileImage(false);
    setProfileSuccessMessage('');
    setProfileErrorMessage('');
  };

  const getProfileImagePreviewUrl = () => {
    if (!profileImage) return '';
    if (profileImage.startsWith('data:image/')) return profileImage;
    return profileImage;
  };

  const handlePasswordInputChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPasswordFieldErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const resetPasswordForm = () => {
    setPasswordForm({ password: '', confirmPassword: '' });
    setPasswordFieldErrors({});
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();

    setPasswordErrorMessage('');
    setPasswordSuccessMessage('');

    const errors = {};
    if (!passwordForm.password) {
      errors.password = `${t('changePassword.newPassword')} ${t('validation.isRequired')}`;
    } else if (passwordForm.password.length < 6) {
      errors.password = t('changePassword.errors.minLength');
    }

    if (!passwordForm.confirmPassword) {
      errors.confirmPassword = `${t('changePassword.confirmPassword')} ${t('validation.isRequired')}`;
    } else if (passwordForm.password !== passwordForm.confirmPassword) {
      errors.confirmPassword = t('changePassword.errors.passwordMismatch');
    }

    if (Object.keys(errors).length > 0) {
      setPasswordFieldErrors(errors);
      if (errors.confirmPassword && passwordForm.password && passwordForm.confirmPassword) {
        setPasswordErrorMessage(errors.confirmPassword);
      } else if (errors.password) {
        setPasswordErrorMessage(errors.password);
      } else {
        setPasswordErrorMessage(t('validation.pleaseFillRequired'));
      }
      return;
    }

    setIsPasswordSubmitting(true);

    try {
      const authToken = localStorage.getItem('authToken');
      if (!authToken) {
        setPasswordErrorMessage(t('changePassword.errors.noAuthToken'));
        return;
      }

      await LoginService.changePassword({ password: passwordForm.password });

      setPasswordSuccessMessage(t('changePassword.success.changed'));
      resetPasswordForm();
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || error?.message === 'Unauthorized') {
        localStorage.removeItem('authToken');
        navigate('/');
        return;
      }

      setPasswordErrorMessage(error?.message || t('changePassword.errors.generic'));
    } finally {
      setIsPasswordSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title={t('profile.title')}
        subtitle={t('profile.subtitle')}
        icon={faUser}
      />

      <section className="panel">
        <div className="panel-body">
          <div className="tabs">
            <ul className="nav nav-tabs">
              <li className={activeTab === 'info' ? 'active' : ''}>
                <a
                  href="#profile-info"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('info');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {t('profile.tabs.info')}
                </a>
              </li>
              <li className={activeTab === 'security' ? 'active' : ''}>
                <a
                  href="#profile-security"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('security');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {t('profile.tabs.security')}
                </a>
              </li>
              <li className={activeTab === 'photo' ? 'active' : ''}>
                <a
                  href="#profile-photo"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveTab('photo');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {t('profile.tabs.photo')}
                </a>
              </li>
            </ul>

            <div className="tab-content">
              {activeTab === 'info' && (
                <div className="tab-pane active">
                  <form
                    id="profileInfoForm"
                    onSubmit={handleProfileSubmit}
                    className="modern-form profile-info-form"
                  >
                    <div className="form-group">
                      <label htmlFor="profileEmail">
                        <i className="fa fa-envelope"></i> {t('profile.email')} (Persona)
                      </label>
                      <input
                        id="profileEmail"
                        name="email"
                        type="email"
                        className="form-control"
                        value={profileForm.email}
                        onChange={handleProfileChange}
                        placeholder={t('profile.emailPlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="profilePersonFullName">
                        <i className="fa fa-user-tag"></i> {t('people.fullName')}
                      </label>
                      <input
                        id="profilePersonFullName"
                        name="fullName"
                        type="text"
                        className="form-control"
                        value={profileForm.fullName}
                        onChange={handleProfileChange}
                        placeholder={t('people.fullNamePlaceholder')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="profileDocumentNumber">
                        <i className="fa fa-id-card-alt"></i> {t('people.documentNumber')}
                      </label>
                      <input
                        id="profileDocumentNumber"
                        name="documentNumber"
                        type="text"
                        className="form-control"
                        value={profileForm.documentNumber}
                        onChange={handleProfileChange}
                        placeholder={t('people.documentNumberPlaceholder')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="profilePhone">
                        <i className="fa fa-phone"></i> {t('people.phone')}
                      </label>
                      <input
                        id="profilePhone"
                        name="phone"
                        type="text"
                        className="form-control"
                        value={profileForm.phone}
                        onChange={handleProfileChange}
                        placeholder={t('people.phonePlaceholder')}
                      />
                    </div>

                  </form>
                </div>
              )}

              {activeTab === 'photo' && (
                <div className="tab-pane active">
                  <div className="modern-form profile-info-form">
                    <div className="form-group">
                      <label>
                        <i className="fa fa-image"></i> {t('profile.photo.title')}
                      </label>

                      {!isEditingProfileImage && typeof profileImage === 'string' && profileImage.trim() !== '' ? (
                        <div className="profile-image-preview-block">
                          <img
                            src={getProfileImagePreviewUrl()}
                            alt={t('profile.photo.title')}
                            className="profile-photo-preview"
                          />
                          <button
                            type="button"
                            className="btn btn-secondary image-preview-action"
                            onClick={() => setIsEditingProfileImage(true)}
                          >
                            {t('profile.photo.changeImage')}
                          </button>
                        </div>
                      ) : (
                        <ImageUploaderWithCrop onImageChange={(imageData) => {
                          setProfileImage(imageData);
                          setIsEditingProfileImage(true);
                        }} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="tab-pane active">
                  <form
                    id="profileSecurityForm"
                    onSubmit={handlePasswordSubmit}
                    className="modern-form profile-security-form"
                  >
                    <div className="form-group">
                      <label htmlFor="securityNewPassword">
                        <i className="fa fa-lock"></i> {t('changePassword.newPassword')} <span className="required-field">*</span>
                      </label>
                      <PasswordInputField
                        id="securityNewPassword"
                        name="password"
                        value={passwordForm.password}
                        onChange={handlePasswordInputChange}
                        placeholder={t('changePassword.enterNewPassword')}
                        className={`form-control login-input ${passwordFieldErrors.password ? 'has-error' : ''}`}
                        wrapperClassName="login-input-wrapper"
                        ariaInvalid={!!passwordFieldErrors.password}
                        autoComplete="new-password"
                        toggleLabels={{
                          show: t('common.showPassword'),
                          hide: t('common.hidePassword'),
                        }}
                      />
                      {passwordFieldErrors.password && (
                        <div className="field-error-message">{passwordFieldErrors.password}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="securityConfirmPassword">
                        <i className="fa fa-lock"></i> {t('changePassword.confirmPassword')} <span className="required-field">*</span>
                      </label>
                      <PasswordInputField
                        id="securityConfirmPassword"
                        name="confirmPassword"
                        value={passwordForm.confirmPassword}
                        onChange={handlePasswordInputChange}
                        placeholder={t('changePassword.confirmNewPassword')}
                        className={`form-control login-input ${passwordFieldErrors.confirmPassword ? 'has-error' : ''}`}
                        wrapperClassName="login-input-wrapper"
                        ariaInvalid={!!passwordFieldErrors.confirmPassword}
                        autoComplete="new-password"
                        toggleLabels={{
                          show: t('common.showPassword'),
                          hide: t('common.hidePassword'),
                        }}
                      />
                      {passwordFieldErrors.confirmPassword && (
                        <div className="field-error-message">{passwordFieldErrors.confirmPassword}</div>
                      )}
                    </div>

                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="panel-footer panel-footer-actions" style={{ textAlign: 'right', padding: '15px' }}>
          {activeTab === 'security' ? (
            <>
              <button
                type="button"
                className="btn btn-default"
                onClick={resetPasswordForm}
                disabled={isPasswordSubmitting}
                style={{ marginRight: '10px' }}
              >
                <i className="fa fa-undo"></i> {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                form="profileSecurityForm"
                disabled={isPasswordSubmitting}
              >
                <i className="fa fa-check"></i> {isPasswordSubmitting ? t('changePassword.changing') : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-default"
                onClick={handleProfileReset}
                style={{ marginRight: '10px' }}
              >
                <i className="fa fa-undo"></i> {t('common.cancel')}
              </button>
              <button
                type={activeTab === 'info' ? 'submit' : 'button'}
                className="btn btn-primary"
                form={activeTab === 'info' ? 'profileInfoForm' : undefined}
                onClick={activeTab === 'photo' ? saveProfile : undefined}
              >
                <i className="fa fa-save"></i> {t('common.save')}
              </button>
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default Profile;

